import { ColumnLayout, Container, Header, KeyValuePairs, LineChart, Link } from '@amzn/awsui-components-console';
import { useMemo } from 'react';
import type { LfMetricsResultFragment } from '../../code-generated/graphqlOperations.generated';

interface LaminarFlowGraphsProps {
  readonly data: LfMetricsResultFragment[];
}

export function LaminarFlowGraphs(props: LaminarFlowGraphsProps) {
  return (
    <div>
      <LaminarFlowDataflowDetails {...props} />
      <br />
      <ColumnLayout columns={2}>
        <LaminarFlowLatencyGraph {...props} />
        <LaminarFlowLossGraph {...props} />
      </ColumnLayout>
    </div>
  );
}

function LaminarFlowDataflowDetails(props: LaminarFlowGraphsProps) {
  const title = 'Laminar Flow Contacts';
  const [contactCount] = useMemo(() => {
    const foundContacts = new Set<string>();
    for (const entry of props.data) {
      if (entry.sourceRegion == null || entry.destinationRegion == null) {
        continue;
      }
      const contactKey = entry.contact_id + entry.sourceRegion + entry.destinationRegion;

      // Skip over any contacts that we have counted already.
      if (foundContacts.has(contactKey)) {
        continue;
      }
      foundContacts.add(contactKey);
    }
    return [foundContacts.size];
  }, [props.data]);

  return (
    <Container header={<Header variant='h3'>{title}</Header>}>
      <KeyValuePairs
        items={[
          {
            label: 'Total Laminar Flow Contacts',
            value: contactCount,
          },
        ]}
      />
      <br />
    </Container>
  );
}

function LaminarFlowLossGraph(props: LaminarFlowGraphsProps) {
  const title = 'Laminar Flow Data Loss (Per Contact)';
  const [packetsLost, packetsLostPercent, xRangeMax, xRangeMin, metricToContactMap] = useMemo(() => {
    const totalPacketsLost: { x: Date; y: number }[] = [];
    const totalPacketsLostPercent: { x: Date; y: number }[] = [];
    let updatedXRangeMin: Date | undefined = undefined;
    let updatedXRangeMax: Date | undefined = undefined;
    const metricToContactMap = new Map<string, string>();

    for (const elem of props.data) {
      if (
        elem.timeStampStart == null ||
        elem.timeStampEnd == null ||
        elem.lfEgressStageMetricsV2?.metric__packets_lost_sequence_count?.total == null ||
        elem.lfEgressStageMetricsV2.metric__last_input_sequence_count?.max == null
      ) {
        continue;
      }
      const metricDate = new Date(elem.timeStampStart + 'Z');
      const totalPacketLostMetric = {
        x: metricDate,
        y: elem.lfEgressStageMetricsV2.metric__packets_lost_sequence_count.total,
      };
      const totalPacketsLostPercentMetric = {
        x: metricDate,
        y:
          (elem.lfEgressStageMetricsV2.metric__packets_lost_sequence_count.total /
            elem.lfEgressStageMetricsV2.metric__last_input_sequence_count.max) *
            100 || 0,
      };

      totalPacketsLost.push(totalPacketLostMetric);
      totalPacketsLostPercent.push(totalPacketsLostPercentMetric);

      // This is just a solution to allow metrics to map to the contact id's so we can link to them in GSOC.
      // It is not the most elegant but it allows per metric observation within the charts.
      metricToContactMap.set(JSON.stringify(totalPacketLostMetric), elem.contact_id);
      metricToContactMap.set(JSON.stringify(totalPacketsLostPercentMetric), elem.contact_id);

      if (updatedXRangeMin == null || updatedXRangeMin > metricDate) {
        updatedXRangeMin = metricDate;
      }
      if (updatedXRangeMax == null || updatedXRangeMax < metricDate) {
        updatedXRangeMax = metricDate;
      }
    }
    return [totalPacketsLost, totalPacketsLostPercent, updatedXRangeMax, updatedXRangeMin, metricToContactMap];
  }, [props.data]);

  return (
    <Container header={<Header variant='h3'>{title}</Header>}>
      <LineChart
        xDomain={xRangeMin && xRangeMax ? [xRangeMin, xRangeMax] : undefined}
        xScaleType='time'
        xTitle='Time (UTC)'
        yTitle={title}
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        detailPopoverSeriesContent={({ series, x, y }) => {
          const key = JSON.stringify({ x: x, y: y });
          const contactId = metricToContactMap.get(key);
          return {
            key: (
              <Link external={true} href={`/contact/${contactId}`}>
                {series.title} - {contactId}
              </Link>
            ),
            value: y,
          };
        }}
        series={[
          {
            title: 'Packets Lost Count',
            type: 'line',
            data: packetsLost,
          },
          {
            title: 'Packets Lost Percent',
            type: 'line',
            data: packetsLostPercent,
          },
        ]}
      />
    </Container>
  );
}

function LaminarFlowLatencyGraph(props: LaminarFlowGraphsProps) {
  const title = 'Laminar Flow Egress Latency (Per Contact) - Seconds';
  const [latencyMinAverage, latencyMaxAverage, xRangeMax, xRangeMin, metricToContactMap] = useMemo(() => {
    const latencyMinAverage: { x: Date; y: number }[] = [];
    const latencyMaxAverage: { x: Date; y: number }[] = [];
    const metricToContactMap = new Map<string, string>();
    let updatedXRangeMin: Date | undefined = undefined;
    let updatedXRangeMax: Date | undefined = undefined;
    for (const elem of props.data) {
      if (
        elem.timeStampStart == null ||
        elem.timeStampEnd == null ||
        elem.lfEgressStageMetricsV2?.metric__timestamp_delta_sec_max?.avg == null ||
        elem.lfEgressStageMetricsV2.metric__timestamp_delta_sec_min?.avg == null
      ) {
        continue;
      }
      const metricDate = new Date(elem.timeStampStart + 'Z');
      const latencyMinMetric = { x: metricDate, y: elem.lfEgressStageMetricsV2.metric__timestamp_delta_sec_min.avg };
      const latencyMaxMetric = { x: metricDate, y: elem.lfEgressStageMetricsV2.metric__timestamp_delta_sec_max.avg };
      latencyMinAverage.push(latencyMinMetric);
      latencyMaxAverage.push(latencyMaxMetric);

      // This is just a solution to allow metrics to map to the contact id's so we can link to them in GSOC.
      // It is not the most elegant but it allows per metric observation within the charts.
      metricToContactMap.set(JSON.stringify(latencyMinMetric), elem.contact_id);
      metricToContactMap.set(JSON.stringify(latencyMaxMetric), elem.contact_id);

      if (updatedXRangeMin == null || updatedXRangeMin > metricDate) {
        updatedXRangeMin = metricDate;
      }
      if (updatedXRangeMax == null || updatedXRangeMax < metricDate) {
        updatedXRangeMax = metricDate;
      }
    }
    return [latencyMinAverage, latencyMaxAverage, updatedXRangeMax, updatedXRangeMin, metricToContactMap];
  }, [props.data]);

  return (
    <Container header={<Header variant='h3'>{title}</Header>}>
      <LineChart
        xDomain={xRangeMin && xRangeMax ? [xRangeMin, xRangeMax] : undefined}
        xScaleType='time'
        xTitle='Time (UTC)'
        yTitle={title}
        detailPopoverSeriesContent={({ series, x, y }) => {
          const key = JSON.stringify({ x: x, y: y });
          const contactId = metricToContactMap.get(key);
          return {
            key: (
              <Link external={true} href={`/contact/${contactId}`}>
                {series.title} - {contactId}
              </Link>
            ),
            value: y,
          };
        }}
        series={[
          {
            title: 'Latency Min Average',
            type: 'line',
            data: latencyMinAverage,
          },
          {
            title: 'Latency Max Average',
            type: 'line',
            data: latencyMaxAverage,
          },
        ]}
      />
    </Container>
  );
}
